import { useNavigate } from 'react-router-dom';
import { Scale } from 'lucide-react';
import shell from '@/styles/authShell.module.css';

/**
 * The split canvas shared by Login, SignupWizard, ForgotPassword and
 * ResetPassword: a dark brand column on the left, the form on the right.
 *
 * The brand column is supplementary by design — it carries no heading, so the
 * form's own <h1> stays the page's first and only one. `headline` is rendered
 * as a paragraph for that reason; pass JSX with <em> to accent a phrase.
 *
 * Terminal status pages keep the centred `.page` / `.centre` card instead. See
 * the block comment above `.split` in authShell.module.css.
 */
const AuthSplitLayout = ({
  testId,
  eyebrow,
  headline,
  lede,
  steps,
  pills,
  note,
  topRight,
  wide = false,
  children,
}) => {
  const navigate = useNavigate();

  return (
    <div className={shell.split} data-testid={testId}>
      <section className={shell.brand}>
        <button
          type="button"
          className={shell.logo}
          onClick={() => navigate('/')}
          data-testid="logo"
          aria-label="MyCaseLoan home"
        >
          <Scale size={26} aria-hidden="true" />
          <span className={shell.logoText}>MyCaseLoan</span>
        </button>

        <div className={shell.brandBody}>
          {eyebrow && <span className={shell.eyebrow}>{eyebrow}</span>}
          {headline && <p className={shell.bigline}>{headline}</p>}
          {lede && <p className={shell.lede}>{lede}</p>}

          {steps && (
            <ol className={shell.ladder}>
              {steps.map((step, i) => (
                <li key={step.title} className={shell.ladderItem}>
                  <span className={shell.ladderNum} aria-hidden="true">{i + 1}</span>
                  <div>
                    <strong>{step.title}</strong>
                    <p>{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}

          {pills && (
            <ul className={shell.pills}>
              {pills.map((pill) => <li key={pill}>{pill}</li>)}
            </ul>
          )}
        </div>

        {note && <p className={shell.brandFoot}>{note}</p>}
      </section>

      <section className={shell.panel}>
        <div className={shell.panelTop}>{topRight}</div>
        <div className={`${shell.formCol} ${wide ? shell.formColWide : ''}`}>
          {children}
        </div>
      </section>
    </div>
  );
};

export default AuthSplitLayout;
